import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import {safeInternalReturnUrl} from '@gatewee/services';
import {TranslateService} from '@ngx-translate/core';
import {TuiAlertService} from '@taiga-ui/core';
import {
  DomainPageSettings,
  DomainSettingKey,
  DomainStyleSettings,
} from '@gatewee/types/domain-setting';
import {AuthService} from '../../../_services/auth/auth.service';
import {SettingsService} from '@gatewee/services/core/settings.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  standalone: false,
})
export class LoginComponent implements OnInit {
  email = '';
  password = '';
  hide_show = true;
  style = {logo: ''};
  branding: DomainPageSettings | null = null;

  showSignupButton = false;

  constructor(
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly alerts: TuiAlertService,
    private translate: TranslateService,
    private settingsService: SettingsService,
    private readonly sanitizer: DomSanitizer,
  ) {}

  ngOnInit(): void {
    this.style = (this.settingsService.get(
      'branding',
      DomainSettingKey.STYLE,
    ) as DomainStyleSettings) || {
      logo: '',
    };

    this.branding = this.settingsService.get(
      'branding',
      DomainSettingKey.PAGE,
    ) as DomainPageSettings | null;

    if (this.authService.isAuthenticated()) {
      void this.router.navigateByUrl(this.postLoginUrl());
    }
    const allowLegal = this.settingsService.get(
      'businessRules',
      DomainSettingKey.ALLOW_LEGAL_PERSON_SIGNUP,
    ) as boolean;

    const allowNatural = this.settingsService.get(
      'businessRules',
      DomainSettingKey.ALLOW_NATURAL_PERSON_SIGNUP,
    ) as boolean;
    this.showSignupButton = allowLegal || allowNatural;
  }

  onEmailChange(value: string | number | object | null): void {
    this.email = typeof value === 'string' ? value : '';
  }

  onPasswordChange(value: string | number | object | null): void {
    this.password = typeof value === 'string' ? value : '';
  }

  passwordHide(): void {
    this.hide_show = !this.hide_show;
  }

  get hasSidePanel(): boolean {
    return !!this.branding?.login?.sidePanel?.backgroundImage;
  }

  get hasAppStore(): boolean {
    return !!this.branding?.login?.mobileApp?.appStoreUrl;
  }

  get hasGooglePlay(): boolean {
    return !!this.branding?.login?.mobileApp?.googlePlayUrl;
  }

  get hasMobileApp(): boolean {
    return this.hasAppStore || this.hasGooglePlay;
  }

  get loginSidePanelHeadlineHtml(): SafeHtml | null {
    const raw = this.branding?.login?.sidePanel?.headline;
    return raw ? this.sanitizer.bypassSecurityTrustHtml(raw) : null;
  }

  private postLoginUrl(): string {
    return safeInternalReturnUrl(
      this.route.snapshot.queryParamMap.get('returnUrl'),
      '/panel',
    );
  }

  login(): void {
    if (!this.email || !this.password) {
      console.error(
        this.translate.instant('features.auth.loginToast.requiredCredentials'),
      );
      return;
    }

    this.authService.login(this.email, this.password).subscribe({
      next: () => {
        void this.router.navigateByUrl(this.postLoginUrl()).then(() => {
          this.alerts
            .open(
              this.translate.instant('features.auth.loginToast.loginSuccess'),
              {label: 'Success', appearance: 'success'},
            )
            .subscribe();
        });
      },
      error: () => {
        this.alerts
          .open(this.translate.instant('features.auth.loginToast.loginError'), {
            label: 'Error',
            appearance: 'error',
          })
          .subscribe();
      },
    });
  }
}
